'use client';
import { getPendingInvitation } from '#pplx/frontend/apps/ask/queries/enterprise-queries/getPendingInvitation.js';
import { getPendingInvitationKey } from '#pplx/frontend/apps/ask/queries/query-keys/enterpriseKeys.js';
import { getHomepageUpsellsQueryKey } from '#pplx/frontend/apps/ask/queries/query-keys/upsellKeys.js';
import { useIsEnterpriseUser } from '#pplx/frontend/auth/useIsEnterpriseUser.js';
import { useIsUserLoggedIn } from '#pplx/frontend/auth/userHooks.js';
import { useQuery } from '#pplx/frontend/react-query/useQuery.js';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
/**
 * Hook to fetch the user's pending organization invitation.
 *
 * This hook queries a separate cached endpoint to reduce QPS on the main
 * user organization endpoint. The backend caches results for 1 hour.
 *
 * Only fetches when:
 * - User is logged in
 * - User is NOT already in an organization (checked via session)
 * - External enabled flag is true
 *
 * Caching strategy:
 * - staleTime: 10 minutes - data stays fresh for 10 min before refetch
 * - gcTime: 1 hour - keeps data in memory for 1 hour after unmount
 * - refetchOnWindowFocus: true - checks for new invitations on tab focus
 * - refetchOnMount: false - prevents unnecessary refetches
 */
export function usePendingInvitation({ reason, enabled = true, }) {
    const isUserLoggedIn = useIsUserLoggedIn();
    const isEnterpriseUser = useIsEnterpriseUser();
    const queryClient = useQueryClient();
    const shouldFetch = isUserLoggedIn && !isEnterpriseUser && enabled;
    const { data, isLoading } = useQuery({
        enabled: shouldFetch,
        queryKey: getPendingInvitationKey(),
        queryFn: () => getPendingInvitation({ reason }),
        staleTime: 10 * 60 * 1000, // 10 minutes
        gcTime: 60 * 60 * 1000, // 1 hour
        refetchOnMount: false,
        refetchOnWindowFocus: true,
        refetchOnReconnect: false,
    });
    const invitationUuid = data?.invitation_uuid;
    useEffect(() => {
        if (!invitationUuid) {
            return;
        }
        void queryClient.invalidateQueries({ queryKey: getHomepageUpsellsQueryKey() });
    }, [invitationUuid, queryClient]);
    return {
        pendingInvitation: data ?? null,
        isLoading,
    };
}
//# sourceMappingURL=usePendingInvitation.js.map